/* Homepage motion; native menus remain in vivekcodes.js. */
(() => {
  'use strict';
  const root = document.documentElement;
  if (root.dataset.vcHomeReferenceRuntime === 'true') return;
  root.dataset.vcHomeReferenceRuntime = 'true';
  const mounted = new Map();

  const initialize = page => {
    if (!page || mounted.has(page)) return;
    let active = true;
    const cleanups = [];
    const frames = new Set();
    const timers = new Set();
    const animations = new Set();
    const listen = (target, type, callback, options) => {
      if (!target) return;
      target.addEventListener(type, callback, options);
      cleanups.push(() => target.removeEventListener(type, callback, options));
    };
    const scheduleFrame = callback => {
      if (!active) return 0;
      const id = window.requestAnimationFrame(time => {
        frames.delete(id);
        if (active) callback(time);
      });
      frames.add(id);
      return id;
    };
    const cancelFrame = id => { window.cancelAnimationFrame(id); frames.delete(id); };
    const scheduleTimeout = (callback, delay) => {
      if (!active) return 0;
      const id = window.setTimeout(() => {
        timers.delete(id);
        if (active) callback();
      }, delay);
      timers.add(id);
      return id;
    };
    const cancelTimeout = id => { window.clearTimeout(id); timers.delete(id); };
    const observe = (Constructor, callback, options) => {
      const observer = new Constructor((entries, instance) => {
        if (active) callback(entries, instance);
      }, options);
      cleanups.push(() => observer.disconnect());
      return observer;
    };
    const createIntersectionObserver = (callback, options) => observe(window.IntersectionObserver, callback, options);
    const createResizeObserver = (callback, options) => observe(window.ResizeObserver, callback, options);
    const runAnimation = (element, keyframes, options) => {
      const animation = element.animate(keyframes, options);
      animations.add(animation);
      animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
      return animation;
    };
    const dispose = () => {
      if (!active) return;
      active = false;
      cleanups.splice(0).reverse().forEach(cleanup => cleanup());
      frames.forEach(id => window.cancelAnimationFrame(id));
      timers.forEach(id => window.clearTimeout(id));
      animations.forEach(animation => animation.cancel());
      frames.clear();
      timers.clear();
      animations.clear();
      page.querySelectorAll('.is-dragging,.is-pointer-active,.is-leaving').forEach(element => {
        element.classList.remove('is-dragging', 'is-pointer-active', 'is-leaving');
      });
      page.querySelectorAll('.dh-marquee-group').forEach(row => { row.style.willChange = ''; });
      page.classList.remove('dh-motion-ready');
      delete page.dataset.referenceInteractionsReady;
      mounted.delete(page);
    };
    mounted.set(page, dispose);
    page.dataset.referenceInteractionsReady = 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const capture = document.documentElement.hasAttribute('data-local-capture') || new URLSearchParams(window.location.search).has('capture');
    if (capture) page.querySelectorAll('img[loading="lazy"]').forEach(image => { image.loading = 'eager'; });
    const motionAllowed = () => !capture && !reducedMotion.matches;
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const sliderSelector = '.dh-work-track, .dh-problem-track, .dh-review-track, .dh-stat-track, .dh-results-track, [data-carousel]';

    const slot = document.querySelector('.dh-header-slot');
    const header = slot?.querySelector('.site-header');
    if (header) {
      let headerFrame = 0, menuTop = -1;
      const updateHeader = () => {
        headerFrame = 0;
        const scrolled = window.scrollY > 50;
        header.classList.toggle('is-scrolled', scrolled);
        slot.classList.toggle('is-scrolled', scrolled);
        const top = Math.max(0, Math.round((header.getBoundingClientRect().bottom + 1) * 100) / 100);
        if (top !== menuTop) {
          menuTop = top;
          header.style.setProperty('--dh-menu-top', `${top}px`);
        }
      };
      const queueHeader = () => { if (!headerFrame) headerFrame = scheduleFrame(updateHeader); };
      listen(window, 'scroll', queueHeader, {passive: true});
      listen(window, 'resize', queueHeader, {passive: true});
      header.querySelectorAll('details').forEach(detail => listen(detail, 'toggle', queueHeader));
      listen(header, 'transitionend', event => { if (event.target === header && event.propertyName === 'top') queueHeader(); });
      if (window.ResizeObserver) createResizeObserver(queueHeader).observe(header);
      cleanups.push(() => {
        header.classList.remove('is-scrolled');
        slot.classList.remove('is-scrolled');
        header.style.removeProperty('--dh-menu-top');
      });
      updateHeader();
    }
    const announcement = document.querySelector('.dh-announcement');
    const announcementToggle = announcement?.querySelector('[data-header-marquee-toggle]');
    listen(announcementToggle, 'click', () => {
      const paused = announcement.classList.toggle('is-paused');
      announcementToggle.setAttribute('aria-pressed', String(paused));
      announcementToggle.setAttribute('aria-label', paused ? announcementToggle.dataset.resumeLabel : announcementToggle.dataset.pauseLabel);
    });

    const intro = page.querySelector('#services-intro');
    if (intro && window.ResizeObserver) {
      const measure = () => intro.style.setProperty('--dh-services-height', `${intro.offsetHeight}px`);
      createResizeObserver(measure).observe(intro);
      measure();
      cleanups.push(() => intro.style.removeProperty('--dh-services-height'));
    }

    const initFadeCarousel = track => {
      const cards = [...track.children];
      if (!cards.length) return;
      const controls = track.nextElementSibling?.matches('.dh-track-controls') ? track.nextElementSibling : null;
      const rail = controls?.querySelector('.dh-progress');
      const thumb = rail?.querySelector('span');
      const buttons = [...page.querySelectorAll('[data-track]')].filter(button => button.dataset.track === track.id);
      const pauseButton = controls?.querySelector('[data-review-toggle]');
      const interactionArea = track.parentElement;
      let index = 0;
      let gesture = null;
      let dragging = false;
      let suppressClickUntil = 0;
      let fadeTimer = 0;
      let autoplayTimer = 0;
      let userPaused = false;
      let hovered = false;
      let focused = false;
      let onscreen = false;
      const show = next => {
        const target = clamp(next, 0, cards.length - 1);
        const previous = index;
        index = target;
        cancelTimeout(fadeTimer);
        cards.forEach((card, position) => {
          const active = position === index;
          card.classList.toggle('is-active', active);
          card.classList.toggle('is-leaving', motionAllowed() && position === previous && position !== index);
          card.setAttribute('aria-hidden', String(!active));
          card.inert = !active;
        });
        if (motionAllowed()) fadeTimer = scheduleTimeout(() => cards.forEach(card => card.classList.remove('is-leaving')), 1050);
        buttons.forEach(button => { button.disabled = Number(button.dataset.direction) < 0 ? index === 0 : index === cards.length - 1; });
        if (rail && thumb) {
          const fraction = 1 / cards.length;
          thumb.style.width = `${fraction * 100}%`;
          thumb.style.transform = `translateX(${rail.clientWidth * fraction * index}px)`;
          rail.setAttribute('aria-valuenow', String(index + 1));
        }
      };
      const canAutoplay = () => cards.length > 1 && motionAllowed() && onscreen && !document.hidden && !hovered && !focused && !userPaused;
      const updateAutoplay = () => {
        cancelTimeout(autoplayTimer);
        autoplayTimer = 0;
        const active = canAutoplay();
        track.setAttribute('aria-live', active ? 'off' : 'polite');
        if (pauseButton) {
          const paused = userPaused || !motionAllowed();
          pauseButton.setAttribute('aria-pressed', String(paused));
          pauseButton.setAttribute('aria-label', paused ? 'Play reviews' : 'Pause reviews');
          pauseButton.disabled = !motionAllowed() || cards.length <= 1;
          const label = pauseButton.querySelector('[data-review-label]');
          if (label) label.textContent = paused ? 'Play reviews' : 'Pause reviews';
          else pauseButton.textContent = paused ? 'Play reviews' : 'Pause reviews';
        }
        if (!active) return;
        autoplayTimer = scheduleTimeout(() => {
          autoplayTimer = 0;
          if (!canAutoplay()) { updateAutoplay(); return; }
          show((index + 1) % cards.length);
          updateAutoplay();
        }, 4000);
      };
      const interactShow = next => {
        userPaused = true;
        show(next);
        updateAutoplay();
      };
      buttons.forEach(button => listen(button, 'click', () => interactShow(index + (Number(button.dataset.direction) || 1))));
      listen(track, 'keydown', event => {
        if (event.target !== track) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); interactShow(index + (event.key === 'ArrowRight' ? 1 : -1)); }
        else if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); interactShow(event.key === 'End' ? cards.length - 1 : 0); }
      });
      listen(track, 'pointerdown', event => {
        if (event.button !== 0 || event.target.closest('button,input,textarea,select,summary')) return;
        gesture = {id: event.pointerId, x: event.clientX, y: event.clientY};
        dragging = false;
      });
      listen(track, 'pointermove', event => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (!dragging && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          dragging = true;
          track.setPointerCapture(event.pointerId);
          track.classList.add('is-dragging');
        }
        if (dragging) event.preventDefault();
      });
      const release = event => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        if (dragging) {
          suppressClickUntil = performance.now() + 180;
          userPaused = true;
          if (event.type !== 'pointercancel' && Math.abs(dx) > 35) show(index + (dx < 0 ? 1 : -1));
          updateAutoplay();
        }
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        gesture = null;
        dragging = false;
        track.classList.remove('is-dragging');
      };
      listen(track, 'pointerup', release);
      listen(track, 'pointercancel', release);
      listen(window, 'pointerup', release, {passive: true});
      listen(track, 'click', event => { if (performance.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); } }, true);
      listen(track, 'dragstart', event => { if (event.target.closest('a,img')) event.preventDefault(); });
      if (rail && thumb) {
        rail.removeAttribute('aria-hidden');
        rail.setAttribute('role', 'scrollbar');
        rail.setAttribute('aria-orientation', 'horizontal');
        rail.setAttribute('aria-controls', track.id);
        rail.setAttribute('aria-valuemin', '1');
        rail.setAttribute('aria-valuemax', String(cards.length));
        rail.setAttribute('aria-label', 'Client testimonial position');
        rail.tabIndex = 0;
        const select = event => {
          const rect = rail.getBoundingClientRect();
          interactShow(Math.floor(clamp((event.clientX - rect.left) / rect.width, 0, .999) * cards.length));
        };
        listen(rail, 'pointerdown', event => { if (event.button === 0) { event.preventDefault(); rail.setPointerCapture(event.pointerId); select(event); } });
        listen(rail, 'pointermove', event => { if (rail.hasPointerCapture(event.pointerId)) { event.preventDefault(); select(event); } });
        listen(rail, 'pointerup', event => { if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId); });
        listen(rail, 'keydown', event => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); interactShow(index + (event.key === 'ArrowRight' ? 1 : -1)); }
          else if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); interactShow(event.key === 'End' ? cards.length - 1 : 0); }
        });
      }
      listen(pauseButton, 'click', () => { userPaused = !userPaused; updateAutoplay(); });
      listen(interactionArea, 'pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        hovered = true;
        updateAutoplay();
      });
      listen(interactionArea, 'pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        hovered = false;
        updateAutoplay();
      });
      listen(interactionArea, 'focusin', () => { focused = true; updateAutoplay(); });
      listen(interactionArea, 'focusout', event => {
        if (event.relatedTarget && interactionArea.contains(event.relatedTarget)) return;
        focused = false;
        updateAutoplay();
      });
      const updateVisibility = visible => {
        if (onscreen === visible) return;
        onscreen = visible;
        updateAutoplay();
      };
      if (window.IntersectionObserver) {
        const visibilityObserver = createIntersectionObserver(entries => entries.forEach(entry => updateVisibility(entry.isIntersecting)), {threshold: .1});
        visibilityObserver.observe(track);
      } else {
        const measureVisibility = () => {
          const rect = track.getBoundingClientRect();
          updateVisibility(rect.bottom > 0 && rect.top < window.innerHeight);
        };
        listen(window, 'scroll', measureVisibility, {passive: true});
        listen(window, 'resize', measureVisibility, {passive: true});
        measureVisibility();
      }
      listen(document, 'visibilitychange', updateAutoplay);
      if (window.ResizeObserver) createResizeObserver(() => show(index)).observe(track);
      listen(reducedMotion, 'change', () => { show(index); updateAutoplay(); });
      show(0);
      updateAutoplay();
    };

    page.querySelectorAll(sliderSelector).forEach(track => {
      if (track.dataset.carouselEffect === 'fade') { initFadeCarousel(track); return; }
      const controls = track.nextElementSibling?.matches('.dh-track-controls')
        ? track.nextElementSibling
        : page.querySelector(`[data-controls-for="${CSS.escape(track.id)}"]`);
      const rail = controls?.querySelector('.dh-progress');
      const thumb = rail?.querySelector('span');
      const buttons = [...page.querySelectorAll('[data-track]')].filter(button => button.dataset.track === track.id);
      let pointer = null;
      let dragged = false;
      let suppressClickUntil = 0;
      let scrollbarPointer = null;
      let scrollFrame = 0;
      const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
      const currentScroll = () => clamp(track.scrollLeft, 0, maxScroll());
      const cards = () => [...track.children].filter(element => !element.hasAttribute('data-carousel-duplicate'));
      const positions = () => {
        const left = track.getBoundingClientRect().left;
        return cards().map(card => clamp(card.getBoundingClientRect().left - left + currentScroll(), 0, maxScroll()));
      };
      const behavior = () => motionAllowed() ? 'smooth' : 'auto';
      const refresh = () => {
        scrollFrame = 0;
        const max = maxScroll();
        const scroll = currentScroll();
        const fraction = max > 1 ? clamp(track.clientWidth / track.scrollWidth, .05, 1) : 1;
        if (track.matches('[data-work-tags]')) {
          const tagWindow = track.closest('.dh-tag-window');
          tagWindow?.classList.toggle('has-tags-before', max > 1 && scroll > 1);
          tagWindow?.classList.toggle('has-tags-after', max > 1 && scroll < max - 1);
        }
        if (rail && thumb) {
          const travel = rail.clientWidth * (1 - fraction);
          thumb.style.width = `${fraction * 100}%`;
          thumb.style.transform = `translateX(${max > 1 ? travel * scroll / max : 0}px)`;
          rail.setAttribute('aria-valuenow', String(max > 1 ? Math.round(scroll / max * 100) : 0));
          rail.setAttribute('aria-disabled', String(max <= 1));
          rail.tabIndex = max > 1 ? 0 : -1;
        }
        buttons.forEach(button => {
          const previous = Number(button.dataset.direction) < 0;
          button.disabled = max <= 1 || (previous ? scroll <= 1 : scroll >= max - 1);
        });
      };
      const queueRefresh = () => { if (!scrollFrame) scrollFrame = scheduleFrame(refresh); };
      const move = direction => {
        const stops = [...new Set(positions().concat(maxScroll()))];
        const current = currentScroll();
        const target = direction > 0
          ? stops.find(position => position > current + 3) ?? maxScroll()
          : [...stops].reverse().find(position => position < current - 3) ?? 0;
        track.scrollTo({left: target, behavior: behavior()});
      };

      buttons.forEach(button => listen(button, 'click', () => move(Number(button.dataset.direction) || 1)));
      listen(track, 'scroll', queueRefresh, {passive: true});
      listen(track, 'keydown', event => {
        if (event.target !== track || event.altKey || event.metaKey || event.ctrlKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); track.scrollTo({left: event.key === 'End' ? maxScroll() : 0, behavior: behavior()});
        }
      });

      listen(track, 'pointerdown', event => {
        const nestedTrack = event.target.closest('[data-carousel]');
        if (nestedTrack && nestedTrack !== track && track.contains(nestedTrack)) return;
        if (event.pointerType === 'touch' || event.button !== 0 || event.target.closest('button,input,textarea,select,summary')) return;
        pointer = {id: event.pointerId, x: event.clientX, y: event.clientY, scroll: currentScroll(), lastX: event.clientX, lastTime: performance.now(), velocity: 0};
        dragged = false;
      });
      listen(track, 'pointermove', event => {
        if (!pointer || pointer.id !== event.pointerId) return;
        const dx = event.clientX - pointer.x;
        const dy = event.clientY - pointer.y;
        if (!dragged && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
          dragged = true;
          track.setPointerCapture(event.pointerId);
          track.classList.add('is-dragging');
        }
        if (!dragged) return;
        event.preventDefault();
        const now = performance.now();
        const elapsed = now - pointer.lastTime;
        if (elapsed > 0) pointer.velocity = (pointer.lastX - event.clientX) / elapsed;
        pointer.lastX = event.clientX;
        pointer.lastTime = now;
        track.scrollLeft = clamp(pointer.scroll - dx, 0, maxScroll());
      });
      const release = event => {
        if (!pointer || (event && pointer.id !== event.pointerId)) return;
        if (dragged) {
          suppressClickUntil = performance.now() + 180;
          if (event?.type === 'pointerup' && motionAllowed() && performance.now() - pointer.lastTime < 90 && Math.abs(pointer.velocity) > .08) {
            track.scrollTo({left: clamp(currentScroll() + pointer.velocity * 160, 0, maxScroll()), behavior: 'smooth'});
          }
        }
        if (track.hasPointerCapture(pointer.id)) track.releasePointerCapture(pointer.id);
        pointer = null;
        dragged = false;
        track.classList.remove('is-dragging');
      };
      listen(track, 'pointerup', release);
      listen(track, 'pointercancel', release);
      listen(track, 'lostpointercapture', release);
      listen(window, 'pointerup', release, {passive: true});
      listen(track, 'click', event => {
        if (performance.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
      }, true);
      listen(track, 'dragstart', event => { if (event.target.closest('a,img')) event.preventDefault(); });

      if (rail && thumb) {
        rail.setAttribute('role', 'scrollbar');
        rail.setAttribute('aria-orientation', 'horizontal');
        rail.setAttribute('aria-controls', track.id);
        rail.setAttribute('aria-valuemin', '0');
        rail.setAttribute('aria-valuemax', '100');
        rail.setAttribute('aria-label', `${track.getAttribute('aria-label') || 'Carousel'} scroll position`);
        rail.removeAttribute('aria-hidden');
        rail.tabIndex = 0;
        listen(rail, 'pointerdown', event => {
          if (event.button !== 0 || maxScroll() <= 1) return;
          event.preventDefault();
          const box = rail.getBoundingClientRect();
          const thumbBox = thumb.getBoundingClientRect();
          const onThumb = event.clientX >= thumbBox.left && event.clientX <= thumbBox.right;
          scrollbarPointer = {id: event.pointerId, offset: onThumb ? event.clientX - thumbBox.left : thumbBox.width / 2};
          rail.setPointerCapture(event.pointerId);
          rail.classList.add('is-dragging');
          const travel = box.width - thumbBox.width;
          if (travel > 0) track.scrollLeft = clamp((event.clientX - box.left - scrollbarPointer.offset) / travel, 0, 1) * maxScroll();
        });
        listen(rail, 'pointermove', event => {
          if (!scrollbarPointer || scrollbarPointer.id !== event.pointerId) return;
          event.preventDefault();
          const box = rail.getBoundingClientRect();
          const travel = box.width - thumb.getBoundingClientRect().width;
          if (travel > 0) track.scrollLeft = clamp((event.clientX - box.left - scrollbarPointer.offset) / travel, 0, 1) * maxScroll();
        });
        const releaseScrollbar = event => {
          if (!scrollbarPointer || scrollbarPointer.id !== event.pointerId) return;
          if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
          scrollbarPointer = null;
          rail.classList.remove('is-dragging');
        };
        listen(rail, 'pointerup', releaseScrollbar);
        listen(rail, 'pointercancel', releaseScrollbar);
        listen(rail, 'lostpointercapture', releaseScrollbar);
        listen(rail, 'keydown', event => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
          else if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); track.scrollTo({left: event.key === 'End' ? maxScroll() : 0, behavior: behavior()}); }
          else if (event.key === 'PageUp' || event.key === 'PageDown') { event.preventDefault(); track.scrollBy({left: track.clientWidth * (event.key === 'PageDown' ? 1 : -1), behavior: behavior()}); }
        });
      }
      if (window.ResizeObserver) createResizeObserver(queueRefresh).observe(track);
      else listen(window, 'resize', queueRefresh, {passive: true});
      track.querySelectorAll('img').forEach(image => listen(image, 'load', queueRefresh, {once: true}));
      listen(reducedMotion, 'change', queueRefresh);
      refresh();
    });

    const initLogoScroll = viewport => {
      const row = viewport.querySelector('.dh-marquee-group');
      if (!row) return;
      let frame = 0;
      let needsMeasure = true;
      let focused = false;
      let touchActive = false;
      let touchManual = false;
      let touchGesture = null;
      let suppressLogoClickUntil = 0;
      let manual = null;
      let current = 0;
      let lastTime = 0;
      let metrics = {top: 0, height: 0, viewportHeight: 0, distance: 0};
      const stop = () => {
        if (frame) cancelFrame(frame);
        frame = 0;
        lastTime = 0;
        row.style.willChange = '';
      };
      const paint = () => { row.style.transform = 'translate3d(' + current.toFixed(3) + 'px,0,0)'; };
      const measure = () => {
        const rect = viewport.getBoundingClientRect();
        metrics = {
          top: rect.top + window.scrollY,
          height: rect.height,
          viewportHeight: window.innerHeight,
          distance: Math.max(0, row.scrollWidth - viewport.clientWidth)
        };
        needsMeasure = false;
        viewport.tabIndex = metrics.distance > 1 ? 0 : -1;
      };
      const desired = () => -metrics.distance * clamp(
        (window.scrollY + metrics.viewportHeight - metrics.top) / (metrics.viewportHeight + metrics.height || 1), 0, 1
      );
      const inView = () => metrics.top + metrics.height > window.scrollY && metrics.top < window.scrollY + metrics.viewportHeight;
      const setManual = next => {
        if (manual === next) return;
        stop();
        current = next ? 0 : -viewport.scrollLeft;
        if (!next) viewport.scrollLeft = 0;
        manual = next;
        viewport.classList.toggle('is-logo-manual', next);
        viewport.classList.toggle('is-logo-motion', !next);
        paint();
      };
      const step = now => {
        frame = 0;
        if (needsMeasure) measure();
        setManual(!motionAllowed() || focused || touchManual);
        if (manual || document.hidden) { stop(); return; }
        const target = desired();
        if (!inView()) {
          current = target;
          paint();
          stop();
          return;
        }
        const elapsed = lastTime ? clamp(now - lastTime, 1, 64) : 16;
        lastTime = now;
        current += (target - current) * (1 - Math.exp(-elapsed / 190));
        if (Math.abs(target - current) < .15) {
          current = target;
          paint();
          stop();
          return;
        }
        row.style.willChange = 'transform';
        paint();
        frame = scheduleFrame(step);
      };
      const queue = () => {
        if (!frame && !document.hidden) frame = scheduleFrame(step);
      };
      const refresh = () => { needsMeasure = true; queue(); };
      const revealFocusedLink = target => {
        if (!(target instanceof Element) || target === viewport) return;
        const item = target.closest('a');
        if (!item || !row.contains(item)) return;
        const viewBox = viewport.getBoundingClientRect();
        const itemBox = item.getBoundingClientRect();
        if (itemBox.left < viewBox.left) viewport.scrollLeft -= viewBox.left - itemBox.left + 12;
        else if (itemBox.right > viewBox.right) viewport.scrollLeft += itemBox.right - viewBox.right + 12;
      };
      if (!viewport.hasAttribute('role')) viewport.setAttribute('role', 'group');
      if (!viewport.hasAttribute('aria-label')) viewport.setAttribute('aria-label', 'Selected project brands. Scroll horizontally to explore.');
      listen(viewport, 'focusin', event => {
        focused = true;
        setManual(true);
        revealFocusedLink(event.target);
      });
      listen(viewport, 'focusout', event => {
        if (event.relatedTarget && viewport.contains(event.relatedTarget)) return;
        focused = false;
        touchManual = false;
        queue();
      });
      listen(viewport, 'keydown', event => {
        if (event.target !== viewport || event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          viewport.scrollBy({left: viewport.clientWidth * .6 * (event.key === 'ArrowRight' ? 1 : -1), behavior: motionAllowed() ? 'smooth' : 'auto'});
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          viewport.scrollTo({left: event.key === 'End' ? metrics.distance : 0, behavior: motionAllowed() ? 'smooth' : 'auto'});
        }
      });
      listen(viewport, 'pointerdown', event => {
        if ((event.pointerType !== 'touch' && event.pointerType !== 'pen') || manual || !motionAllowed()) return;
        touchActive = true;
        touchGesture = {id: event.pointerId, x: event.clientX, y: event.clientY, scroll: -current, horizontal: false};
      }, {passive: true});
      listen(viewport, 'pointermove', event => {
        if (!touchGesture || touchGesture.id !== event.pointerId) return;
        const dx = event.clientX - touchGesture.x;
        const dy = event.clientY - touchGesture.y;
        if (!touchGesture.horizontal) {
          if (Math.abs(dy) > 8 && Math.abs(dy) >= Math.abs(dx)) {
            touchGesture = null;
            touchActive = false;
            return;
          }
          if (Math.abs(dx) <= 8 || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
          touchGesture.horizontal = true;
          touchManual = true;
          setManual(true);
          viewport.scrollLeft = touchGesture.scroll;
          viewport.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        viewport.scrollLeft = clamp(touchGesture.scroll - dx, 0, metrics.distance);
      }, {passive: false});
      const releaseTouch = event => {
        if (!touchGesture || touchGesture.id !== event.pointerId) return;
        if (touchGesture.horizontal) suppressLogoClickUntil = performance.now() + 180;
        if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
        touchGesture = null;
        touchActive = false;
      };
      listen(window, 'pointerup', releaseTouch, {passive: true});
      listen(window, 'pointercancel', releaseTouch, {passive: true});
      listen(viewport, 'click', event => {
        if (performance.now() < suppressLogoClickUntil) { event.preventDefault(); event.stopPropagation(); }
      }, true);
      listen(window, 'scroll', () => {
        if (!touchActive && !focused) touchManual = false;
        queue();
      }, {passive: true});
      listen(window, 'resize', refresh, {passive: true});
      if (window.ResizeObserver) {
        const observer = createResizeObserver(refresh);
        observer.observe(viewport);
        observer.observe(row);
      }
      row.querySelectorAll('img').forEach(image => {
        if (!image.complete) listen(image, 'load', refresh, {once: true});
      });
      document.fonts?.ready.then(refresh);
      listen(document, 'visibilitychange', () => { if (document.hidden) stop(); else refresh(); });
      listen(reducedMotion, 'change', () => { touchManual = false; refresh(); });
      measure();
      setManual(!motionAllowed());
      if (!manual) { current = desired(); paint(); }
      queue();
    };
    page.querySelectorAll('[data-logo-scroll]').forEach(initLogoScroll);

    page.querySelectorAll('[data-pointer-pill]').forEach(pill => {
      const surface = pill.closest('[data-pointer-surface]') || pill.closest('.dh-work-image,.dh-hero-media') || pill.parentElement;
      if (!surface) return;
      pill.setAttribute('aria-hidden', 'true');
      let frame = 0;
      let position = null;
      const update = () => {
        frame = 0;
        if (!position) return;
        const rect = surface.getBoundingClientRect();
        pill.style.setProperty('--pointer-x', `${clamp(position.x - rect.left, 0, rect.width)}px`);
        pill.style.setProperty('--pointer-y', `${clamp(position.y - rect.top, 0, rect.height)}px`);
      };
      listen(surface, 'pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        position = {x: event.clientX, y: event.clientY};
        update();
        surface.classList.add('is-pointer-active');
      });
      listen(surface, 'pointermove', event => {
        if (event.pointerType !== 'mouse') return;
        position = {x: event.clientX, y: event.clientY};
        if (!frame) frame = scheduleFrame(update);
      });
      listen(surface, 'pointerleave', () => {
        surface.classList.remove('is-pointer-active');
        position = null;
        if (frame) { cancelFrame(frame); frame = 0; }
      });
    });

    const countTargets = [...page.querySelectorAll('[data-count-to]')];
    const runningCounts = new Map();
    const countFinal = target => {
      const value = Number(String(target.dataset.countTo).replaceAll(',', ''));
      if (!Number.isFinite(value)) return null;
      const decimals = Math.min(3, (String(target.dataset.countTo).split('.')[1] || '').length);
      const formatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: decimals, maximumFractionDigits: decimals});
      const prefix = target.dataset.countPrefix || '';
      const suffix = target.dataset.countSuffix || '';
      return {value, formatter, prefix, suffix, text: `${prefix}${formatter.format(value)}${suffix}`};
    };
    const finishCounts = () => runningCounts.forEach((count, target) => {
      cancelFrame(count.frame);
      target.textContent = count.final.text;
      runningCounts.delete(target);
    });
    const animateCount = target => {
      const final = countFinal(target);
      if (!final) return;
      target.setAttribute('aria-label', final.text);
      target.setAttribute('aria-live', 'off');
      if (!motionAllowed()) { target.textContent = final.text; return; }
      const duration = clamp(Number(target.dataset.countDuration || 1400), 300, 4000);
      const start = performance.now();
      const count = {frame: 0, final};
      const step = now => {
        const progress = clamp((now - start) / duration, 0, 1);
        const eased = 1 - (1 - progress) ** 3;
        const value = final.value * eased;
        target.textContent = `${final.prefix}${final.formatter.format(Math.round(value * 1000) / 1000)}${final.suffix}`;
        if (progress < 1 && motionAllowed()) count.frame = scheduleFrame(step);
        else { target.textContent = final.text; runningCounts.delete(target); }
      };
      runningCounts.set(target, count);
      count.frame = scheduleFrame(step);
    };
    if (countTargets.length && window.IntersectionObserver && motionAllowed()) {
      const counterObserver = createIntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        counterObserver.unobserve(entry.target);
        animateCount(entry.target);
      }), {threshold: .45});
      countTargets.forEach(target => {
        const final = countFinal(target);
        if (final) { target.setAttribute('aria-label', final.text); target.textContent = `${final.prefix}0${final.suffix}`; counterObserver.observe(target); }
      });
    } else countTargets.forEach(animateCount);

    page.querySelectorAll('.dh-services details').forEach(detail => {
      const content = detail.querySelector('.dh-service-panel') || detail.querySelector('.dh-service-tags');
      if (!content) return;
      let animation = null;
      listen(detail, 'toggle', () => {
        animation?.cancel();
        content.style.overflow = '';
        if (!detail.open || !motionAllowed()) return;
        content.style.overflow = 'hidden';
        animation = runAnimation(content, [{height: '0px', opacity: 0}, {height: `${content.getBoundingClientRect().height}px`, opacity: 1}], {
          duration: 300, easing: 'cubic-bezier(.2,.7,.25,1)'
        });
        animation.onfinish = () => { content.style.overflow = ''; animation = null; };
        animation.oncancel = () => { content.style.overflow = ''; };
      });
    });

    page.querySelectorAll('[data-marquee-toggle]').forEach(button => {
      const marquee = button.closest('.dh-marquee,.dh-announcement,.dh-client-strip,.dh-tech-strip');
      if (!marquee) return;
      let userPaused = false;
      const update = () => {
        const paused = userPaused || capture || reducedMotion.matches;
        marquee.classList.toggle('is-paused', paused);
        button.setAttribute('aria-pressed', String(paused));
        button.setAttribute('aria-label', paused ? 'Play animation' : 'Pause animation');
        button.title = paused ? 'Play animation' : 'Pause animation';
        button.disabled = capture || reducedMotion.matches;
        const label = button.querySelector('[data-marquee-label]');
        if (label) label.textContent = paused ? '▶' : 'Ⅱ';
        else button.textContent = paused ? '▶' : 'Ⅱ';
      };
      listen(button, 'click', () => { userPaused = !userPaused; update(); });
      listen(reducedMotion, 'change', update);
      update();
    });

    page.querySelectorAll('[data-tour-toggle]').forEach(button => {
      const tour = button.closest('[data-store-tour]') || page.querySelector('[data-store-tour]');
      if (!tour) return;
      let userPaused = false;
      const update = () => {
        const paused = userPaused || capture || reducedMotion.matches;
        tour.classList.toggle('is-paused', paused);
        button.setAttribute('aria-pressed', String(paused));
        button.setAttribute('aria-label', paused ? 'Play store tour' : 'Pause store tour');
        button.disabled = capture || reducedMotion.matches;
        const label = button.querySelector('[data-tour-label]');
        if (label) label.textContent = paused ? 'Play store tour' : 'Pause store tour';
        else button.textContent = paused ? 'Play store tour' : 'Pause store tour';
      };
      listen(button, 'click', () => { userPaused = !userPaused; update(); });
      listen(reducedMotion, 'change', update);
      update();
    });

    // Targets remain visible if JavaScript or motion is unavailable.
    const revealTargets = [...page.querySelectorAll('[data-reveal]')];
    if (revealTargets.length && window.IntersectionObserver && motionAllowed()) {
      page.classList.add('dh-motion-ready');
      const observer = createIntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          entry.target.classList.add('is-visible');
        });
      }, {threshold: .08, rootMargin: '0px 0px -24px 0px'});
      revealTargets.forEach(target => {
        if (target.dataset.revealDelay) target.style.setProperty('--reveal-delay', `${Number(target.dataset.revealDelay)}ms`);
        observer.observe(target);
      });
    } else revealTargets.forEach(target => target.classList.add('is-visible'));
    listen(reducedMotion, 'change', () => {
      if (!reducedMotion.matches) return;
      finishCounts();
      countTargets.forEach(target => { const final = countFinal(target); if (final) target.textContent = final.text; });
      revealTargets.forEach(target => target.classList.add('is-visible'));
    });

    cleanups.push(finishCounts);
  };

  const mount = () => document.querySelectorAll('#main-content.dh-page').forEach(initialize);
  const isAffected = (target, page) => target instanceof Element && (target === page || target.contains(page) || page.contains(target));
  const resetSection = event => {
    [...mounted].forEach(([page, dispose]) => {
      if (!page.isConnected || isAffected(event.target, page)) dispose();
    });
    if (event.type === 'shopify:section:load') mount();
  };
  document.addEventListener('shopify:section:load', resetSection);
  document.addEventListener('shopify:section:unload', resetSection);
  window.addEventListener('pagehide', () => [...mounted.values()].forEach(dispose => dispose()));
  window.addEventListener('pageshow', mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, {once: true});
  else mount();
})();
